import { Pause, Play } from "lucide-react";
import { useMemo, useState } from "react";
import { type TranscriptSegment, type TranscriptSpeaker } from "@/lib/api";
import { formatTimecode } from "@/lib/format";
import { useAudioFragment } from "@/lib/useAudioFragment";

/** How many turns the panel lists before it offers the rest. */
const TURNS_SHOWN = 25;

/** A turn's length in milliseconds, and zero for one the aligner left untimed. */
function turnMs(turn: TranscriptSegment): number {
  return turn.startsAtMs === null || turn.endsAtMs === null ? 0 : turn.endsAtMs - turn.startsAtMs;
}

/**
 * Three turns from across the meeting — early, middle, late — each the longest in its
 * stretch.
 *
 * The point is *spread*, not quality. Three samples from the same two minutes prove only
 * that those two minutes are consistent; three from the beginning, middle and end of an
 * hour are what tell you whether one cluster held one person all the way through. Within
 * each stretch the longest turn wins for the ordinary reason — a sentence identifies a
 * voice and "yeah" does not.
 *
 * Empty for a voice with fewer than three timed turns, where the list below says
 * everything a spread could.
 */
function spread(turns: TranscriptSegment[]): TranscriptSegment[] {
  const timed = turns.filter((turn) => turn.startsAtMs !== null && turn.endsAtMs !== null);
  if (timed.length < 3) return [];

  const byTime = [...timed].sort((a, b) => a.startsAtMs! - b.startsAtMs!);
  const stretch = Math.ceil(byTime.length / 3);

  const picks: TranscriptSegment[] = [];
  for (let at = 0; at < byTime.length; at += stretch) {
    const bucket = byTime.slice(at, at + stretch);
    picks.push(bucket.reduce((best, turn) => (turnMs(turn) > turnMs(best) ? turn : best), bucket[0]!));
  }

  return picks;
}

/**
 * What a voice is actually made of (SPCH-6) — the turns behind the row, and what those
 * turns amount to in the gallery.
 *
 * Longest first, which is the order for the job this panel exists to do: deciding whether
 * every turn in here is the same person. The clearest evidence goes to the top, and the
 * timecode on each row says where in the meeting it came from, so chronology is not lost
 * by sorting away from it.
 */
export function SpeakerTurns({
  speaker,
  confirmed,
  colour,
  turns,
  player,
}: {
  speaker: TranscriptSpeaker;
  confirmed: boolean;
  colour: string;
  turns: TranscriptSegment[];
  player: ReturnType<typeof useAudioFragment>;
}) {
  const [all, setAll] = useState(false);

  const longestFirst = useMemo(
    () => [...turns].sort((a, b) => turnMs(b) - turnMs(a) || a.sequence - b.sequence),
    [turns],
  );
  const samples = useMemo(() => spread(turns), [turns]);

  const shown = all ? longestFirst : longestFirst.slice(0, TURNS_SHOWN);

  return (
    <div
      id={`turns-${speaker.id}`}
      className="flex flex-col gap-[var(--space-3)] border-t border-[var(--border-subtle)] pt-[var(--space-3)]"
    >
      {turns.length === 0 ? (
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Nothing in this recording is attributed to this voice.
        </p>
      ) : (
        <>
          {samples.length > 0 && (
            <div className="flex flex-wrap items-center gap-[var(--space-2)]">
              <span className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">Hear a spread:</span>
              {samples.map((turn) => (
                <button
                  key={`spread-${turn.id}`}
                  type="button"
                  onClick={() => player.play({ id: turn.id, startMs: turn.startsAtMs!, endMs: turn.endsAtMs })}
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] bg-[var(--surface-2)] px-2.5 py-1 text-[length:var(--text-xs)] font-medium text-[var(--text-primary)] transition hover:brightness-125"
                >
                  {player.playing === turn.id ? (
                    <Pause size={11} strokeWidth={2.5} />
                  ) : (
                    <Play size={11} strokeWidth={2.5} />
                  )}
                  {formatTimecode(turn.startsAtMs!)}
                </button>
              ))}
            </div>
          )}

          <ul aria-label={`Turns of ${speaker.confirmedName ?? speaker.label ?? "this voice"}`} className="flex flex-col">
            {shown.map((turn) => {
              const playing = player.playing === turn.id;
              const length = turnMs(turn);

              return (
                <li
                  key={turn.id}
                  className="flex items-start gap-[var(--space-3)] border-b border-[var(--border-subtle)] py-[var(--space-2)] last:border-b-0"
                >
                  <button
                    type="button"
                    disabled={turn.startsAtMs === null}
                    onClick={() => player.play({ id: turn.id, startMs: turn.startsAtMs!, endMs: turn.endsAtMs })}
                    aria-label={
                      playing
                        ? "Stop this turn"
                        : `Play the turn at ${turn.startsAtMs === null ? "an unknown time" : formatTimecode(turn.startsAtMs)}`
                    }
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)] text-[var(--vl-bridge-soft)] transition hover:brightness-125 disabled:opacity-40"
                  >
                    {playing ? <Pause size={11} strokeWidth={2.5} /> : <Play size={11} strokeWidth={2.5} />}
                  </button>

                  <span className="mt-0.5 w-24 shrink-0 text-[length:var(--text-2xs)] tabular-nums text-[var(--text-muted)]">
                    {turn.startsAtMs === null ? "—" : formatTimecode(turn.startsAtMs)}
                    {length > 0 && <> · {(length / 1000).toFixed(1)}s</>}
                  </span>

                  <span className="min-w-0 flex-1 text-[length:var(--text-xs)] leading-relaxed text-[var(--text-secondary)]">
                    {turn.text}
                  </span>
                </li>
              );
            })}
          </ul>

          {longestFirst.length > TURNS_SHOWN && (
            <button
              type="button"
              onClick={() => setAll((shownAll) => !shownAll)}
              className="self-start text-[length:var(--text-xs)] font-semibold transition hover:brightness-125"
              style={{ color: colour }}
            >
              {all ? "Show fewer turns" : `Show all ${longestFirst.length} turns`}
            </button>
          )}
        </>
      )}

      {/*
        What confirming this voice would actually teach the gallery (SPCH-5). Said here
        because this is the panel somebody reads just before they decide, and because
        the answer is sometimes "nothing" — after a reassignment invalidated the samples
        (0037), or before `voiceprint` has run at all.
      */}
      <p className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
        {speaker.sampleCount === 0 ? (
          <span className="text-[var(--warning)]">
            No voice samples. Naming this voice still records who it is, but it teaches the gallery nothing until the
            recording is restarted at <span className="font-semibold">voiceprint</span>.
          </span>
        ) : (
          <>
            {speaker.sampleCount} voice sample{speaker.sampleCount === 1 ? "" : "s"} ·{" "}
            {confirmed ? "in the gallery" : "added to the gallery when you confirm"}
          </>
        )}
      </p>
    </div>
  );
}
